import { api } from './api';

export interface Booking {
  _id: string;
  clientId: {
    _id: string;
    firstName: string;
    lastName: string;
    avatarColor?: string;
  };
  serviceId: {
    _id: string;
    name: string;
    color: string;
  };

  startTime: string;
  endTime: string;
  duration: number;
  timezone: string;

  status: BookingStatus;
  cancellationReason?: string;
  cancellationNote?: string;

  serviceName: string;
  servicePrice: number;
  servicePricingType: string;
  serviceColor: string;

  clientName: string;
  clientEmail: string;

  meetingType: 'online' | 'in_person' | 'phone' | 'flexible';
  meetingLocation?: string;
  meetingUrl?: string;
  meetingInstructions?: string;

  notes: BookingNote[];
  internalNotes?: string;

  formattedDate: string;
  formattedTimeRange: string;
  isUpcoming: boolean;
  isPast: boolean;

  createdAt: string;
  updatedAt: string;
}

export type BookingStatus = 'scheduled' | 'completed' | 'cancelled' | 'no_show' | 'rescheduled';

export interface BookingNote {
  _id: string;
  content: string;
  createdBy: {
    _id: string;
    profile: {
      firstName: string;
      lastName: string;
    };
  };
  createdAt: string;
}

export interface CalendarEvent {
  id: string;
  title: string;
  start: string;
  end: string;
  backgroundColor: string;
  borderColor: string;
  extendedProps: {
    clientName: string;
    serviceName: string;
    status: BookingStatus;
  };
}

export interface TimeSlot {
  start: string;
  end: string;
  available: boolean;
}

export interface BookingsListParams {
  page?: number;
  limit?: number;
  status?: BookingStatus | 'all';
  clientId?: string;
  serviceId?: string;
  startDate?: string;
  endDate?: string;
  upcoming?: 'true' | 'false';
  sortBy?: 'startTime' | 'createdAt' | 'clientName' | 'serviceName';
  sortOrder?: 'asc' | 'desc';
}

export interface BookingsListResponse {
  bookings: Booking[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
    hasMore: boolean;
  };
}

export interface BookingStats {
  today: number;
  thisWeek: number;
  upcoming: number;
  total: number;
  byStatus: Record<BookingStatus, { count: number; revenue: number }>;
}

export interface CreateBookingData {
  clientId: string;
  serviceId: string;
  startTime: string;
  timezone?: string;
  meetingType?: 'online' | 'in_person' | 'phone' | 'flexible';
  meetingLocation?: string;
  meetingUrl?: string;
  addGoogleMeet?: boolean;
  internalNotes?: string;
}

export interface UpdateBookingData {
  startTime?: string;
  meetingType?: 'online' | 'in_person' | 'phone' | 'flexible';
  meetingLocation?: string;
  meetingUrl?: string;
  internalNotes?: string;
}

// API Functions
export const bookingsApi = {
  // List bookings
  list: async (params: BookingsListParams = {}): Promise<BookingsListResponse> => {
    const response = await api.get('/bookings', { params });
    return response.data.data;
  },

  // Get single booking
  get: async (id: string): Promise<Booking> => {
    const response = await api.get(`/bookings/${id}`);
    return response.data.data.booking;
  },

  // Get calendar events
  getCalendarEvents: async (start: string, end: string): Promise<CalendarEvent[]> => {
    const response = await api.get('/bookings/calendar', { params: { start, end } });
    return response.data.data.events;
  },

  // Get booking stats
  getStats: async (): Promise<BookingStats> => {
    const response = await api.get('/bookings/stats');
    return response.data.data;
  },

  // Get availability
  getAvailability: async (serviceId: string, date: string): Promise<{ slots: TimeSlot[] }> => {
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const response = await api.get('/bookings/availability', { params: { serviceId, date, timezone } });
    return response.data.data;
  },

  // Create booking
  create: async (data: CreateBookingData): Promise<Booking> => {
    const response = await api.post('/bookings', data);
    return response.data.data.booking;
  },

  // Update booking
  update: async (id: string, data: UpdateBookingData): Promise<Booking> => {
    const response = await api.patch(`/bookings/${id}`, data);
    return response.data.data.booking;
  },

  // Update status
  updateStatus: async (
    id: string,
    status: BookingStatus,
    cancellationReason?: string,
    cancellationNote?: string
  ): Promise<Booking> => {
    const response = await api.patch(`/bookings/${id}/status`, {
      status,
      cancellationReason,
      cancellationNote,
    });
    return response.data.data.booking;
  },

  // Reschedule
  reschedule: async (id: string, startTime: string, reason?: string): Promise<Booking> => {
    const response = await api.post(`/bookings/${id}/reschedule`, { startTime, reason });
    return response.data.data.booking;
  },

  // Delete booking
  delete: async (id: string): Promise<void> => {
    await api.delete(`/bookings/${id}`);
  },

  // Add note
  addNote: async (id: string, content: string): Promise<BookingNote> => {
    const response = await api.post(`/bookings/${id}/notes`, { content });
    return response.data.data.note;
  },
};
